import { useQuery } from "@apollo/client/react";
import { Navigate, Route, Routes } from "react-router";

import NotFoundPage from "components/error/NotFoundPage";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import PageWrapper from "components/PageWrapper";
import SubSidebar from "components/SubSidebar";
import SubSidebarSection from "components/SubSidebar/Section";
import SubSidebarSectionItem from "components/SubSidebar/SectionItem";
import SubSidebarSectionTitle from "components/SubSidebar/SectionTitle";
import ViewHeader from "components/ViewHeader";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import Badge from "ds/components/Badge";
import BoxNew from "ds/components/Box";
import useTypedContext from "hooks/useTypedContext";
import useTypedFlags from "hooks/useTypedFlags";
import { AccountContext } from "views/AccountWrapper";

import { SpacesContext } from "../SpacesProvider";
import AccessibilitySettings from "./Accessibility";
import PersonalSettingsApiKeys from "./ApiKeys";
import PersonalSettingsApiKeysLegacy from "./ApiKeys/Legacy";
import CustomizeSidebar from "./CustomizeSidebar";
import DateTimeSettings from "./DateTimeSettings";
import ExternalAccounts from "./ExternalAccounts";
import { GET_PERSONAL_SETTINGS } from "./gql";
import PersonalSettingsGroups from "./Groups";
import MFASettings from "./MFASettings";
import NotificationsCustomization from "./NotificationsCustomization";
import PersonalSettingsSpaces from "./Spaces";
import styles from "./styles.module.css";
import ThemeAppearance from "./ThemeAppearance";

const PersonalSettings = () => {
  const { enableApiKeyCreationForNonRootAdminsFrontend, githubLink, personalApiKeys, slackLink } =
    useTypedFlags();
  const { hasManageableSpaces } = useTypedContext(SpacesContext);
  const { viewer } = useTypedContext(AccountContext);
  const hasIdentity = Boolean(viewer.identity);
  const canSeeExternalAccounts = hasIdentity && (githubLink || slackLink);

  const { data, loading } = useQuery(GET_PERSONAL_SETTINGS, {
    variables: {
      skipApiKeysManagedByNonRootAdminsField:
        !enableApiKeyCreationForNonRootAdminsFrontend || personalApiKeys,
    },
  });

  if (loading && !data?.authorizationScheme) {
    return <PageLayoutSkeleton />;
  }

  const shouldShowLegacyApiKeys =
    enableApiKeyCreationForNonRootAdminsFrontend &&
    data?.apiKeysManagedByNonRootAdmins &&
    hasManageableSpaces;

  const shouldShowApiKeys = personalApiKeys || shouldShowLegacyApiKeys;

  return (
    <>
      <ViewHeader>
        <ViewHeaderWrapper direction="row">
          <ViewHeaderTitle>Personal settings</ViewHeaderTitle>
        </ViewHeaderWrapper>
      </ViewHeader>
      <BoxNew className={styles.content}>
        <SubSidebar>
          <SubSidebarSection>
            <SubSidebarSectionTitle>ACCESS</SubSidebarSectionTitle>
            <SubSidebarSectionItem to="../spaces">Spaces</SubSidebarSectionItem>
            <SubSidebarSectionItem to="../groups">IdP groups</SubSidebarSectionItem>
            {shouldShowApiKeys && (
              <SubSidebarSectionItem to="../api-keys">
                {personalApiKeys ? (
                  <BoxNew gap="small" align="center">
                    Personal API keys
                    <Badge variant="blue" type="strong" text="BETA" />
                  </BoxNew>
                ) : (
                  "API keys"
                )}
              </SubSidebarSectionItem>
            )}
          </SubSidebarSection>

          <SubSidebarSection>
            <SubSidebarSectionTitle>AUTHENTICATION</SubSidebarSectionTitle>
            <SubSidebarSectionItem to="../mfa">Multi-factor authentication</SubSidebarSectionItem>
          </SubSidebarSection>

          {canSeeExternalAccounts && (
            <SubSidebarSection>
              <SubSidebarSectionTitle>CONNECTIONS</SubSidebarSectionTitle>
              <SubSidebarSectionItem to="../external-accounts">
                External accounts
              </SubSidebarSectionItem>
            </SubSidebarSection>
          )}

          <SubSidebarSection>
            <SubSidebarSectionTitle>PERSONALIZATION</SubSidebarSectionTitle>
            <SubSidebarSectionItem to="../theme-appearance">Theme appearance</SubSidebarSectionItem>
            <SubSidebarSectionItem to="../sidebar-customization">
              Sidebar customization
            </SubSidebarSectionItem>
            <SubSidebarSectionItem to="../notifications-customization">
              Notifications customization
            </SubSidebarSectionItem>
            <SubSidebarSectionItem to="../accessibility">Accessibility</SubSidebarSectionItem>
            <SubSidebarSectionItem to="../datetime-settings">
              Time preferences
            </SubSidebarSectionItem>
          </SubSidebarSection>
        </SubSidebar>

        <PageWrapper className={styles.wrapper}>
          <Routes>
            <Route index element={<Navigate to="groups" replace />} />
            <Route path="theme-appearance" element={<ThemeAppearance />} />
            <Route path="spaces" element={<PersonalSettingsSpaces />} />
            <Route path="sidebar-customization" element={<CustomizeSidebar />} />
            <Route path="notifications-customization" element={<NotificationsCustomization />} />
            <Route path="accessibility" element={<AccessibilitySettings />} />
            <Route path="datetime-settings" element={<DateTimeSettings />} />
            <Route path="groups" element={<PersonalSettingsGroups />} />
            {personalApiKeys && (
              <Route
                path="api-keys"
                element={
                  <PersonalSettingsApiKeys authorizationScheme={data?.authorizationScheme ?? ""} />
                }
              />
            )}
            {!personalApiKeys && shouldShowLegacyApiKeys && (
              <Route
                path="api-keys"
                element={
                  <PersonalSettingsApiKeysLegacy authorizationScheme={data?.authorizationScheme ?? ""} />
                }
              />
            )}
            <Route path="mfa" element={<MFASettings />} />
            {canSeeExternalAccounts && (
              <Route path="external-accounts" element={<ExternalAccounts />} />
            )}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </PageWrapper>
      </BoxNew>
    </>
  );
};

export default PersonalSettings;
